// The world's sim tier (gate C1): on every sim step @rpg/stream's `Residency` decides which of @rpg/world's chunks are
// resident around the hero (and pinned round the other characters), and applies three layers together, in order:
// the chunk's physics body (its heightfield, trunks, big rocks and placements' colliders) into the @rpg/collision
// world, its tile of the @rpg/nav navmesh (built from the same height function and solids, with a margin for the
// tile's border), and the `world:chunk` event other modules hang their own things on. Unloading runs the other way
// (the event first, "unloading"). Decisions read sim state only, so the sim stays deterministic; the resident set is
// in the save, the colliders come back in the physics snapshot and the nav tiles are rebuilt to match.
//
// Nav tiles in slices (phase C joined): a chunk's load asks for its nav tiles (a chunk is 2 × 2 tiles) from the
// world's own `NavTileSlicer`, which builds them a unit of work per sim step (the things stream's staged jobs: no step
// carries a whole tile) from the chunk's geometry, made at the load; it steps only on steps the things' slicer is
// idle, so a step carries one unit at most. A tile is used from the step its last unit swaps it in: a deterministic
// step (units, never measured time), the same in every run and after a restore, which builds at once the tiles that
// were built at the save and puts the pending ones back in the queue at the unit they had reached. A cut (a teleport)
// and the boot's prime build what they asked for at once. An unloading chunk drops its tiles from both slicers and
// takes them out of the mesh.
// Checks: after every step, every character (the hero, each live enemy) stands over a chunk whose
// colliders are loaded, and the three layers are in step with the resident set; counted for the route bot's report
// and raised as invariants for the harness. Planted defect `collidersOneChunkLate`: the colliders layer is applied
// one chunk late (`Residency.faults.lateLayer`).
import type { GameModule, EventBus } from "@rpg/kernel";
import type { CollisionWorld } from "@rpg/collision";
import { navGeometry, type NavGeometry, type NavShape, type NavTileSlicer, type NavWorld, type SlicerState, type TileXY } from "@rpg/nav";
import { Residency, type ResidencyAnchor, type ResidencySnapshot } from "@rpg/stream";
import type { World } from "@rpg/world";
import type { GameEvents } from "../core";
import type { WorldStreaming } from "./content";

/** Planted defects the world's sim tier owns (each beside what it breaks). */
export const WORLD_SIM_FAULTS = {
  /** A chunk's colliders are added one chunk late (the next chunk's load adds them): the C1 checks must go red. */
  collidersOneChunkLate: false,
  /** A chunk's nav tiles built in one slice (`NavTileSlicer.faults.oneSlice`): a step carries whole tiles again, and the
   *  route's per-step units check must go red. */
  navChunkOneSlice: false,
};

/** A streamed world's nav geometry: each resident chunk's, made when it loads, handed to the navmesh per tile (a tile
 *  is in one chunk; a chunk is `split` × `split` tiles). */
export interface ChunkNavSource {
  /** For `buildNavWorld`'s `source`: the geometry of the chunk a tile is in, or null when it isn't resident. */
  source(tile: TileXY): NavGeometry | null;
  fill(id: string): void;
  drop(id: string): void;
  /** A chunk's tiles. */
  tilesOf(id: string): TileXY[];
}

/** The world's nav geometry per chunk. `tileAt` is the navmesh's (made after this: the navmesh takes `source`). */
export function chunkNavSource(world: World, o: { margin: number; split: number; tileAt: () => (x: number, z: number) => TileXY }): ChunkNavSource {
  const geo = new Map<string, NavGeometry>();
  const size = world.layout.chunk / o.split;
  const tilesOf = (id: string): TileXY[] => {
    const c = world.chunk(id);
    const at = o.tileAt();
    const out: TileXY[] = [];
    for (let j = 0; j < o.split; j++) for (let i = 0; i < o.split; i++) out.push(at(c.area.x0 + (i + 0.5) * size, c.area.z0 + (j + 0.5) * size));
    return out;
  };
  const b = world.layout.bounds;
  return {
    source: (t) => {
      const c = world.chunkAt(b.x0 + (t.x + 0.5) * size, b.z0 + (t.y + 0.5) * size);
      return c ? (geo.get(c.id) ?? null) : null;
    },
    fill: (id) => void geo.set(id, navGeometry(chunkNavShapes(world, id, o.margin))),
    drop: (id) => void geo.delete(id),
    tilesOf,
  };
}

/** The layers, in load order. */
export const WORLD_LAYERS = ["colliders", "nav", "events"] as const;

/** A character the checks watch: a label and where its feet are. */
export interface WorldCharacter {
  label: string;
  x: number;
  z: number;
}

/** The sim tier's checks over a run (diagnostics, not saved). */
export interface WorldSimChecks {
  steps: number;
  /** Steps on which some character stood over a chunk without its colliders. */
  overUnloaded: number;
  /** Steps on which a layer was out of step with the resident set. */
  outOfStep: number;
  first: string | null;
  /** The chunks' nav tiles: asked for, built (swapped in, or come out empty), dropped before their swap, removed with
   *  their chunk, waiting now, and the slices that built them: how many, the most units in one, their mean and worst ms
   *  (counts under SwiftShader or Node, not frame times), the stage of the worst; tiles built at once (a cut, the
   *  boot's prime, a restore). */
  nav: { requested: number; builds: number; dropped: number; removed: number; pending: number; slices: number; maxUnits: number; meanMs: number; maxMs: number; maxStage: string | null; atOnce: number };
}

export interface WorldSim {
  readonly residency: Residency;
  readonly module: GameModule<GameEvents>;
  /** Load what the hero and the pins need now (at the content build, before the first step). */
  prime(hero: ResidencyAnchor, pins: readonly ResidencyAnchor[], reason?: string): void;
  /** The hero was moved by a tool (a warp): load what it needs now (colliders, nav tiles, events), as a cut does. */
  teleported(): void;
  /** Problems now: characters over unloaded chunks, layers out of step (empty when fine). */
  problems(): string[];
  checks(): WorldSimChecks;
  resetChecks(): void;
  /** A chunk's nav tiles. */
  tilesOf(id: string): TileXY[];
  /** The world's tile slicer (chunk tiles). */
  readonly slicer: NavTileSlicer;
  /** Every resident chunk's tiles have been through a job (built or come out empty): none waiting. */
  navSettled(): boolean;
  /** The `world:chunk` events emitted since the last reset. */
  events(): { loaded: number; unloading: number };
}

/** Nav shapes for one chunk: a heightfield over its area grown by `margin`, and every solid within it. */
export function chunkNavShapes(world: World, id: string, margin: number): NavShape[] {
  const h = world.heightsNear(id, margin);
  const out: NavShape[] = [{ kind: "heightfield", rows: h.rows, cols: h.cols, heights: h.heights, size: [h.size, 1, h.size], position: [h.cx, 0, h.cz] }];
  for (const s of world.solidsNear(id, margin)) {
    const p = s.position;
    const sh = s.spec.shape;
    if (s.spec.sensor) continue;
    if (sh.type === "cylinder") out.push({ kind: "cylinder", center: [p.x, p.y, p.z], radius: sh.radius, halfHeight: sh.halfHeight });
    else if (sh.type === "ball") out.push({ kind: "cylinder", center: [p.x, p.y, p.z], radius: sh.radius, halfHeight: sh.radius });
    else if (sh.type === "cuboid") out.push({ kind: "box", center: [p.x, p.y, p.z], halfExtents: [sh.halfExtents.x, sh.halfExtents.y, sh.halfExtents.z], rotation: s.rotation });
    else if (sh.type === "trimesh") out.push({ kind: "mesh", positions: sh.vertices, indices: sh.indices, position: [p.x, p.y, p.z], rotation: s.rotation });
  }
  return out;
}

/** The world's sim tier on a collision world and a streamed navmesh. */
export function createWorldSim(o: {
  world: World;
  collision: CollisionWorld;
  nav: NavWorld;
  /** The chunks' nav geometry (the navmesh's `source`). */
  navSource: ChunkNavSource;
  /** The world's tile slicer over `nav`. */
  slicer: NavTileSlicer;
  /** Another slicer working on the same navmesh this step (the things'): the world's waits while it is busy. */
  busyElsewhere?: () => boolean;
  /** A tile leaving the mesh with its chunk: drop it from other slicers too. */
  tileGone?: (tile: TileXY) => void;
  streaming: WorldStreaming;
  /** The hero's feet and ground velocity. */
  hero(): ResidencyAnchor;
  /** Pins: where other characters stand (and camps waiting for their fighters). */
  pins(): ResidencyAnchor[];
  /** Every character the checks watch. */
  characters(): WorldCharacter[];
}): WorldSim {
  const { world, collision, nav, streaming, slicer } = o;
  let events: EventBus<GameEvents> | null = null;
  /** The `world:chunk` events emitted (diagnostics: not saved). */
  const heard = { loaded: 0, unloading: 0 };
  const key = (t: TileXY): string => `${t.x},${t.y}`;
  /** Tiles through a job since their chunk loaded (built or empty): the check that none is left waiting. */
  const done = new Set<string>();
  const nv = { requested: 0, builds: 0, dropped: 0, removed: 0, slices: 0, maxUnits: 0, sumMs: 0, maxMs: 0, maxStage: null as string | null, atOnce: 0 };
  const tilesOf = (id: string): TileXY[] => o.navSource.tilesOf(id);
  let lastHero: { x: number; z: number } | null = null;
  /** Build every waiting tile now (a cut, the prime). */
  const flush = (): void => {
    for (const t of slicer.flush()) {
      done.add(key(t));
      nv.builds++;
      nv.atOnce++;
    }
  };
  const residency = new Residency(
    world.chunks.map((c) => ({ id: c.id, area: c.area })),
    [
      { name: "colliders", load: (id) => collision.add(world.body(id)), unload: (id) => collision.remove(`chunk:${id}`) },
      {
        name: "nav",
        // the chunk's geometry now (its tiles' jobs read it), its tiles queued: built a unit a step from here
        load: (id) => {
          o.navSource.fill(id);
          const tiles = tilesOf(id);
          slicer.request(tiles);
          nv.requested += tiles.length;
        },
        unload: (id) => {
          for (const t of tilesOf(id)) {
            if (slicer.drop(t)) nv.dropped++;
            o.tileGone?.(t);
            done.delete(key(t));
            if (nav.removeTile(t)) nv.removed++;
          }
          o.navSource.drop(id);
        },
      },
      {
        name: "events",
        load: (id) => {
          heard.loaded++;
          events?.emit("world:chunk", { id, state: "loaded" });
        },
        unload: (id) => {
          heard.unloading++;
          events?.emit("world:chunk", { id, state: "unloading" });
        },
      },
    ],
    { ...streaming.sim, pinRadius: streaming.pinRadius, loadsPerStep: streaming.loadsPerStep, predictS: streaming.predictS, teleportM: streaming.teleportM, graceSteps: 0 },
  );
  const syncFault = (): void => {
    residency.faults.lateLayer = WORLD_SIM_FAULTS.collidersOneChunkLate ? "colliders" : null;
    slicer.faults.oneSlice = WORLD_SIM_FAULTS.navChunkOneSlice;
  };
  syncFault();

  const checks: Omit<WorldSimChecks, "nav"> = { steps: 0, overUnloaded: 0, outOfStep: 0, first: null };
  const overUnloaded = (): string[] => {
    const out: string[] = [];
    for (const ch of o.characters()) {
      const c = world.chunkAt(ch.x, ch.z);
      if (!c) out.push(`world: ${ch.label} outside the world at ${ch.x.toFixed(1)}, ${ch.z.toFixed(1)}`);
      else if (!residency.layerHas("colliders", c.id)) out.push(`world: ${ch.label} over an unloaded chunk ${c.id}`);
    }
    return out;
  };
  const problems = (): string[] => {
    const out = overUnloaded();
    const off = residency.consistent();
    if (off.length) out.push(`world: chunk layers out of step (${off.slice(0, 3).join(", ")}${off.length > 3 ? ", …" : ""})`);
    return out;
  };

  const module: GameModule<GameEvents> = {
    id: "world",
    version: "0.1.0",
    // checked declarations (decision 0077): what it emits
    provides: { events: ["world:chunk"] },
    setup(ctx) {
      events = ctx.events;
      ctx.registerSystem(
        () => {
          syncFault();
          const h = o.hero();
          residency.step(h, o.pins());
          // a cut (a jump past the residency's teleport distance) loaded what it needs at once: its tiles too
          if (lastHero && Math.hypot(h.x - lastHero.x, h.z - lastHero.z) > streaming.teleportM) flush();
          lastHero = { x: h.x, z: h.z };
        },
        { name: "world:stream", order: -50 },
      );
      // the chunks' tiles, a slice a step, after the things' slicer (whose slice comes first when it has one)
      ctx.registerSystem(
        () => {
          if (!slicer.busy() || o.busyElsewhere?.()) return;
          const r = slicer.step();
          nv.slices++;
          nv.maxUnits = Math.max(nv.maxUnits, r.units);
          nv.sumMs += r.ms;
          if (r.ms > nv.maxMs) {
            nv.maxMs = r.ms;
            nv.maxStage = r.stages[0] ?? null;
          }
          for (const t of r.finished) {
            done.add(key(t));
            nv.builds++;
          }
        },
        { name: "world:nav", order: 175 },
      );
      ctx.registerSystem(
        () => {
          checks.steps++;
          if (overUnloaded().length) checks.overUnloaded++;
          const off = residency.consistent();
          if (off.length) checks.outOfStep++;
          if (!checks.first) checks.first = problems()[0] ?? null;
        },
        { name: "world:check", order: 190 },
      );
      ctx.registerSaveSlice({
        serialize: () => ({ seed: world.seed, residency: residency.snapshot(), slicer: slicer.snapshot() }),
        deserialize: (data) => {
          const d = data as { seed: number; residency: ResidencySnapshot; slicer?: SlicerState };
          if (d.seed !== world.seed) throw new Error(`world: the save's world seed ${d.seed} isn't this world's ${world.seed} (open ?seed=${d.seed})`);
          // the colliders come back in the physics snapshot (restored after the kernel's slices) and the events were
          // heard when they happened; the nav tiles are rebuilt to the saved set: those built at the save at once, those
          // still waiting back in the slicer where they were (a save from before slicing: every tile at once)
          const saved = d.slicer ?? { queue: [], job: null };
          const waiting = new Set([...saved.queue.map(([x, y]) => `${x},${y}`), ...(saved.job ? [`${saved.job.tile[0]},${saved.job.tile[1]}`] : [])]);
          slicer.restore({ queue: [], job: null });
          done.clear();
          residency.restore(d.residency, { restored: ["colliders", "events"] });
          for (const t of slicer.pending()) {
            if (waiting.has(key(t))) continue;
            nav.tileJob(t).finish();
            done.add(key(t));
          }
          slicer.restore(saved);
          // a restore is not a cut: the next step doesn't compare the hero with where this page's hero was before the
          // load (a far one would build every queued tile at once, and the restored game would part from the saved one)
          lastHero = null;
        },
      });
    },
  };

  const self: WorldSim = {
    residency,
    module,
    teleported() {
      self.prime(o.hero(), o.pins(), "teleport");
    },
    prime(hero, pins, reason = "boot") {
      syncFault();
      residency.prime(hero, pins, reason);
      flush();
      lastHero = { x: hero.x, z: hero.z };
    },
    problems,
    checks: () => ({
      ...checks,
      nav: { requested: nv.requested, builds: nv.builds, dropped: nv.dropped, removed: nv.removed, pending: slicer.pending().length, slices: nv.slices, maxUnits: nv.maxUnits, meanMs: nv.slices ? nv.sumMs / nv.slices : 0, maxMs: nv.maxMs, maxStage: nv.maxStage, atOnce: nv.atOnce },
    }),
    resetChecks: () => {
      Object.assign(checks, { steps: 0, overUnloaded: 0, outOfStep: 0, first: null });
      Object.assign(nv, { requested: 0, builds: 0, dropped: 0, removed: 0, slices: 0, maxUnits: 0, sumMs: 0, maxMs: 0, maxStage: null, atOnce: 0 });
      heard.loaded = 0;
      heard.unloading = 0;
      residency.resetStats();
    },
    tilesOf,
    slicer,
    navSettled: () => !slicer.busy() && residency.resident().every((id) => tilesOf(id).every((t) => done.has(key(t)))),
    events: () => ({ ...heard }),
  };
  return self;
}
